// Loyola Marymount University: public college page (#2739), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - LMU's Common Data Set 2025-2026 (Office of Institutional Research PDF,
//    posted May 2026): C1 applicants, admits and enrollees for Fall 2025 with
//    the residency table, C8 test policy for Fall 2027, C21/C22 plans with
//    the early decision counts for the Fall 2025 entering class (one count,
//    not split between Early Decision I and II; no early action counts are
//    given). C8E gives the score date as 2/1/2026, a year that does not fit
//    fall 2027 applicants, so no score date is shown.
//  - lmu.edu admissions: First-Year Applicants (deadlines, decision release,
//    deposit and financial aid dates, requirements), the Frequently Asked
//    Questions (test-optional policy) and the LMU Newsroom release of
//    September 28, 2026 announcing the fall 2027 early decision deadlines.
//  - LMU Financial Aid, Undergraduate Cost of Attendance: the 2026-2027
//    on-campus budget. The CDS G1/G5 give 2026-2027 tuition ($68,042) and
//    fees too; the financial aid budget is used because it itemizes every
//    cost in one total.
//  - College Scorecard API, school id 117946 (net price by family income).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'LMU Common Data Set 2025-2026',
        url: 'https://academics.lmu.edu/media/lmuacademics/strategicplanningacademiceffectiveness/officeofinstitutionalresearch/documents/CDS%202025-26_20260518.pdf',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'LMU Undergraduate Admission, First-Year Applicants',
        url: 'https://admission.lmu.edu/learnmore/prospectivestudents/first-yearapplicants/',
        read: '2026-10-01',
    },
    news: {
        label: 'LMU Newsroom, Early Decision Deadlines for Fall 2027 Applicants',
        url: 'https://newsroom.lmu.edu/press-release/loyola-marymount-university-announces-early-decision-deadlines-for-fall-2027-applicants/',
        read: '2026-10-01',
    },
    faq: {
        label: 'LMU Undergraduate Admission, Frequently Asked Questions',
        url: 'https://admission.lmu.edu/learnmore/prospectivestudents/frequentlyaskedquestions/',
        read: '2026-10-01',
    },
    coa: {
        label: 'LMU Financial Aid, Undergraduate Cost of Attendance',
        url: 'https://financialaid.lmu.edu/generalinformation/costofattendance/undergraduatecostofattendance/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?117946-Loyola-Marymount-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'loyola-marymount-university',
    kbId: 'loyola_marymount_university',
    name: 'Loyola Marymount University',
    shortName: 'LMU',
    location: 'Los Angeles, CA',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (LMU identity standards, colors); decoration only.
    branding: {
        primary: '#AB0C2F',
        secondary: '#0076A5',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/loyola_marymount_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 22798,
        admitted: 10992,
        enrolled: 1536,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 12133, admitted: 6673 },
            { group: 'Out-of-state', applied: 7958, admitted: 4166 },
            { group: 'International', applied: 2707, admitted: 153 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'LMU\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early decision',
        note: 'The Common Data Set gives one early decision count, not split between Early Decision I and II, and no counts for early action, which is not binding.',
        year: 'Fall 2025',
        rows: [
            { plan: 'Early Decision', applied: 858, admitted: 364 },
        ],
        source: 'cds',
        where: 'sections C21 and C22',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 15' },
        rows: [
            { plan: 'Early Decision I', deadline: 'November 1', decision: 'Mid-December', binding: true },
            { plan: 'Early Action', deadline: 'November 1', decision: 'Mid-December', binding: false },
            { plan: 'Early Decision II', deadline: 'January 8', decision: 'Mid-February', binding: true },
            { plan: 'Regular Decision', deadline: 'January 15', decision: 'Early March', binding: false },
        ],
        note: 'The FAFSA or California Dream Act Application is due November 1 for Early Decision I, January 10 for Early Action and Early Decision II, and February 1 for Regular Decision. Deposits are due January 12 for Early Decision I, March 16 for Early Decision II and May 1 for Early Action and Regular Decision. The application fee is $75, and one letter of recommendation is required.',
        source: 'deadlines',
        binding: 'Early Decision I and II are binding: if admitted, you enroll at LMU and withdraw your other applications. Early Decision I admits also get an extra LMU scholarship, priority housing selection, priority registration for the first term and priority consideration for programs with limited space. Early Action is not binding; you have until May 1.',
        bindingSource: 'news',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'LMU is test-optional: you choose whether to send SAT or ACT scores. Applicants without scores are not disadvantaged and are still considered for every major, the Honors Program and merit scholarships. If you apply test-optional, scores sent by a testing agency or shown on a transcript are not considered.',
        source: 'faq',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as considered if submitted, not required, for fall 2027 applicants.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'deadlines',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition and fees', amount: 68939 },
            { item: 'Housing and food (average)', amount: 23602 },
            { item: 'Books and supplies', amount: 1305 },
            { item: 'Personal and miscellaneous', amount: 3969 },
            { item: 'Parking and transportation', amount: 1386 },
            { item: 'Federal loan fees', amount: 88 },
        ],
        total: 99289,
        note: 'For a full-time undergraduate living on campus. Housing and food, personal costs and transportation are LMU\'s averages, and the loan fee is the average for students who borrow federal loans. LMU\'s estimate is $102,255 living off campus and $88,638 living with parents or relatives.',
        source: 'coa',
        netPrice: {
            average: 50518,
            byIncome: [
                { income: '$0 to $30,000', amount: 36415 },
                { income: '$30,001 to $48,000', amount: 34172 },
                { income: '$48,001 to $75,000', amount: 33201 },
                { income: '$75,001 to $110,000', amount: 45169 },
                { income: 'Over $110,000', amount: 63737 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from LMU\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
